import React, { useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  TextField,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import {
  CmpAdvertisingOptionFieldsFragment,
  useCreateCmpAdvertisingOptionMutation,
  useDeleteCmpAdvertisingOptionMutation,
  useUpdateCmpAdvertisingOptionMutation,
} from "../../../../graphql/types";
import { commonStyles } from "../../../common/commonStyles";
import {
  centsToCurrencyValue,
  formatCurrencyValueOnBlur,
  sanitizeCurrencyValue,
} from "../../../common/dashboard/currencyUnits";
import { formatCurrency } from "../invoiceFormatting";
import { cpaGold, cpaGoldDark, InvoiceSection } from "../InvoiceMockComponents";
import { parseCurrencyToCents } from "./createInvoiceHelpers";

const DESCRIPTION_MAX_LENGTH = 50;

type EditorState = {
  advertisingOptionId: string | null;
  code: string;
  name: string;
  description: string;
  priceInput: string;
};

// POL-019 (B5): per-field dialog validation (code/name required, price > 0).
type EditorFieldErrors = {
  code?: string;
  name?: string;
  price?: string;
};

const emptyEditorState: EditorState = {
  advertisingOptionId: null,
  code: "",
  name: "",
  description: "",
  priceInput: "",
};

const advertisingFieldSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "6px",
    minHeight: 56,
  },
  "& .MuiInputLabel-root": {
    fontSize: 15,
    fontWeight: 700,
  },
};

const toEditorState = (option: CmpAdvertisingOptionFieldsFragment): EditorState => ({
  advertisingOptionId: option.id,
  code: option.code,
  name: option.name,
  description: option.description || "",
  // POL-005 (B5): canonical 2-decimal dollars string ("300.00"), matching the
  // blur-canonicalized entry shape.
  priceInput: centsToCurrencyValue(option.priceInCents),
});

export const AdvertisingOptionsManager = ({
  advertisingOptions,
  chamberId,
  isCmpInvoiceModuleTestData,
  loading,
  onChanged,
}: {
  advertisingOptions: CmpAdvertisingOptionFieldsFragment[];
  chamberId: string;
  isCmpInvoiceModuleTestData?: boolean;
  loading: boolean;
  onChanged: () => void;
}) => {
  const [editor, setEditor] = useState<EditorState | null>(null);
  const [editorFieldErrors, setEditorFieldErrors] = useState<EditorFieldErrors>({});
  const [optionPendingDelete, setOptionPendingDelete] =
    useState<CmpAdvertisingOptionFieldsFragment | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [createAdvertisingOption, { loading: creating }] =
    useCreateCmpAdvertisingOptionMutation();
  const [updateAdvertisingOption, { loading: updating }] =
    useUpdateCmpAdvertisingOptionMutation();
  const [deleteAdvertisingOption, { loading: deleting }] =
    useDeleteCmpAdvertisingOptionMutation();

  const saving = creating || updating;

  const clearEditorFieldError = (field: keyof EditorFieldErrors) =>
    setEditorFieldErrors((previous) => {
      if (!previous[field]) {
        return previous;
      }

      const next = { ...previous };
      delete next[field];
      return next;
    });

  const handleSave = async () => {
    if (!editor) {
      return;
    }

    const priceInCents = parseCurrencyToCents(editor.priceInput);

    // POL-019 (B5): per-field errors replace the section-level-only Alert for
    // the required-field checks.
    const nextFieldErrors: EditorFieldErrors = {};

    if (!editor.code.trim()) {
      nextFieldErrors.code = "Advertising option code is required.";
    }

    if (!editor.name.trim()) {
      nextFieldErrors.name = "Advertising option name is required.";
    }

    if (priceInCents <= 0) {
      nextFieldErrors.price = "Enter a price greater than $0.00.";
    }

    setEditorFieldErrors(nextFieldErrors);

    if (Object.keys(nextFieldErrors).length > 0) {
      return;
    }

    try {
      setErrorMessage(null);

      if (editor.advertisingOptionId) {
        await updateAdvertisingOption({
          variables: {
            input: {
              chamberId,
              advertisingOptionId: editor.advertisingOptionId,
              code: editor.code.trim(),
              name: editor.name.trim(),
              description: editor.description.trim() || undefined,
              priceInCents,
            },
          },
        });
      } else {
        await createAdvertisingOption({
          variables: {
            input: {
              chamberId,
              code: editor.code.trim(),
              name: editor.name.trim(),
              description: editor.description.trim() || undefined,
              priceInCents,
              isCmpInvoiceModuleTestData: isCmpInvoiceModuleTestData || undefined,
            },
          },
        });
      }

      setEditor(null);
      onChanged();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Advertising option could not be saved."
      );
    }
  };

  const handleConfirmDelete = async () => {
    if (!optionPendingDelete) {
      return;
    }

    try {
      setErrorMessage(null);
      await deleteAdvertisingOption({
        variables: {
          input: { chamberId, advertisingOptionId: optionPendingDelete.id },
        },
      });
      setOptionPendingDelete(null);
      onChanged();
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : "Advertising option could not be deleted."
      );
    }
  };

  return (
    <InvoiceSection
      label="Advertising"
      title="Advertising Options"
      description="Help promote your member's companies through high impact advertising placements."
      variant="plain"
    >
      {errorMessage ? (
        <Alert severity="error" className="mb-4" onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      ) : null}

      <div className="overflow-x-auto rounded-md border border-gray-200">
        <table className="w-full min-w-[760px] border-collapse text-left">
          <thead>
            {/* TICK-3 (B5): mock shows a light-gray header band with dark
                uppercase labels here — only the line-items table is blue. */}
            <tr className="bg-gray-50 text-[12px] font-extrabold uppercase tracking-normal text-gray-700">
              <th className="w-[42px] px-3 py-4" aria-label="Reorder" />
              <th className="px-4 py-4">Code</th>
              <th className="px-4 py-4">Descriptions</th>
              <th className="px-4 py-4 text-right">Price</th>
              <th className="px-4 py-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && advertisingOptions.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                  <CircularProgress size={18} className="mr-2 align-middle" />
                  Loading advertising options...
                </td>
              </tr>
            ) : null}
            {!loading && advertisingOptions.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500">
                  No advertising options yet. Add one to begin.
                </td>
              </tr>
            ) : null}
            {advertisingOptions.map((option) => (
              <tr key={option.id} className="border-t border-gray-100">
                <td className="px-3 py-4 text-center text-gray-400">
                  <DragIndicatorIcon fontSize="small" />
                </td>
                <td className="px-4 py-4 text-sm font-bold text-gray-900">
                  {option.code}
                </td>
                <td className="px-4 py-4 text-sm font-semibold text-gray-700">
                  {option.name}
                </td>
                <td className="px-4 py-4 text-right text-sm font-extrabold text-gray-900">
                  {/* TICK-4 (B5): slash-joined "$300.00/annually" per mock. */}
                  {formatCurrency(option.priceInCents)}
                  <span className="text-xs font-medium text-gray-500">/annually</span>
                </td>
                <td className="px-4 py-4 text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<EditOutlinedIcon />}
                      onClick={() => {
                        setEditorFieldErrors({});
                        setEditor(toEditorState(option));
                      }}
                      sx={{
                        backgroundColor: cpaGold,
                        fontWeight: 800,
                        textTransform: "none",
                        "&:hover": { backgroundColor: cpaGoldDark },
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      size="small"
                      variant="contained"
                      startIcon={<DeleteOutlineIcon />}
                      disabled={deleting}
                      onClick={() => {
                        setErrorMessage(null);
                        setOptionPendingDelete(option);
                      }}
                      sx={{
                        backgroundColor: "#F31723",
                        fontWeight: 800,
                        textTransform: "none",
                        "&:hover": { backgroundColor: "#D90F1A" },
                      }}
                    >
                      Delete
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        onClick={() => {
          setErrorMessage(null);
          setEditorFieldErrors({});
          setEditor({ ...emptyEditorState });
        }}
        className="mt-5 inline-flex min-h-[48px] items-center gap-2 rounded-md px-5 py-2 text-sm font-bold text-white"
        style={{ backgroundColor: cpaGold }}
      >
        <AddIcon fontSize="small" /> Add Advertising Options
      </button>

      <Dialog
        open={!!editor}
        onClose={() => setEditor(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "20px",
            maxWidth: 640,
            px: { xs: 2.5, sm: 4 },
            py: { xs: 3, sm: 4 },
          },
        }}
      >
        {editor ? (
          <>
            <DialogTitle className="relative p-0">
              <IconButton
                aria-label="Close advertising option editor"
                onClick={() => setEditor(null)}
                sx={{
                  backgroundColor: "#F31723",
                  color: "white",
                  height: 34,
                  position: "absolute",
                  right: 0,
                  top: 0,
                  width: 34,
                  "&:hover": { backgroundColor: "#D90F1A" },
                }}
              >
                <CloseIcon sx={{ fontSize: 22 }} />
              </IconButton>
              <span className="block text-[24px] font-extrabold leading-tight text-gray-950">
                {editor.advertisingOptionId
                  ? "Edit Advertising Options"
                  : "Add Advertising Options"}
              </span>
              <span className="mt-2 block text-[15px] font-normal leading-6 text-gray-600">
                {editor.advertisingOptionId
                  ? "Edit the details of advertising options."
                  : "Add the details of the advertising option."}
              </span>
            </DialogTitle>

            <DialogContent className="px-0 pt-5">
              <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-bold text-gray-900">
                    Advertising Option Code <span className="text-red-500">*</span>
                  </label>
                  <TextField
                    placeholder="Enter Advertising Option Code"
                    value={editor.code}
                    sx={advertisingFieldSx}
                    error={Boolean(editorFieldErrors.code)}
                    helperText={editorFieldErrors.code}
                    onChange={({ target }) => {
                      clearEditorFieldError("code");
                      setEditor({ ...editor, code: target.value });
                    }}
                    fullWidth
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-bold text-gray-900">
                    Advertising Option Name <span className="text-red-500">*</span>
                  </label>
                  <TextField
                    placeholder="Enter Advertising Option Name"
                    value={editor.name}
                    sx={advertisingFieldSx}
                    error={Boolean(editorFieldErrors.name)}
                    helperText={editorFieldErrors.name}
                    onChange={({ target }) => {
                      clearEditorFieldError("name");
                      setEditor({ ...editor, name: target.value });
                    }}
                    fullWidth
                  />
                </div>
              </div>

              <div className="mt-5">
                <label className="mb-1 block text-sm font-bold text-gray-900">
                  Advertising Option Description (Optional)
                </label>
                {/* TICK-8 (B5): mock shows the helper sentence above the
                    input and a standalone character counter below it. */}
                <p className="mb-2 text-[13px] leading-5 text-gray-600">
                  Optional brief description to appear for the advertising option.
                </p>
                <TextField
                  placeholder="Enter Advertising Option Description"
                  value={editor.description}
                  sx={advertisingFieldSx}
                  onChange={({ target }) =>
                    setEditor({
                      ...editor,
                      description: target.value.slice(0, DESCRIPTION_MAX_LENGTH),
                    })
                  }
                  fullWidth
                />
                <p className="mt-1 text-xs text-gray-400">
                  {editor.description.length}/{DESCRIPTION_MAX_LENGTH} characters
                </p>
              </div>

              {/* TICK-8 (B5): price input spans the full dialog width per mock. */}
              <div className="mt-5">
                <label className="mb-1 block text-sm font-bold text-gray-900">
                  Advertising Option Price <span className="text-red-500">*</span>
                </label>
                {/* POL-005 (B5): decimal text input (no spinners), "$" prefix
                    kept, blur-canonicalized to 2 decimals; the dollars-string
                    state stays the storage boundary (cents at save). */}
                <TextField
                  placeholder="0.00"
                  value={editor.priceInput}
                  sx={advertisingFieldSx}
                  error={Boolean(editorFieldErrors.price)}
                  helperText={editorFieldErrors.price}
                  inputProps={{ inputMode: "decimal" }}
                  onChange={({ target }) => {
                    clearEditorFieldError("price");
                    setEditor({
                      ...editor,
                      priceInput: sanitizeCurrencyValue(target.value),
                    });
                  }}
                  onBlur={() =>
                    setEditor((previous) =>
                      previous
                        ? {
                            ...previous,
                            priceInput: formatCurrencyValueOnBlur(previous.priceInput),
                          }
                        : previous
                    )
                  }
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">$</InputAdornment>
                    ),
                  }}
                  fullWidth
                />
              </div>
            </DialogContent>

            {/* TICK-8 (B5): Save Advertising Option sits bottom-LEFT per mock. */}
            <DialogActions
              className="px-0 pb-0 pt-7"
              sx={{ justifyContent: "flex-start" }}
            >
              <Button
                variant="contained"
                disabled={saving}
                onClick={() => void handleSave()}
                startIcon={
                  saving ? (
                    <CircularProgress color="inherit" size={18} />
                  ) : undefined
                }
                sx={{
                  backgroundColor: cpaGold,
                  borderRadius: "6px",
                  fontSize: 16,
                  fontWeight: 800,
                  minHeight: 56,
                  minWidth: 300,
                  px: 4,
                  textTransform: "none",
                  "&:hover": { backgroundColor: cpaGoldDark },
                }}
              >
                Save Advertising Option
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>

      <Dialog
        open={!!optionPendingDelete}
        onClose={() => setOptionPendingDelete(null)}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            borderRadius: "22px",
            maxWidth: 620,
            position: "relative",
            px: { xs: 3, sm: 5 },
            py: { xs: 4, sm: 5 },
          },
        }}
      >
        {optionPendingDelete ? (
          <>
            <DialogTitle className="p-0 text-center">
              <IconButton
                aria-label="Close delete confirmation"
                onClick={() => setOptionPendingDelete(null)}
                sx={{
                  backgroundColor: "#F31723",
                  color: "white",
                  height: 38,
                  position: "absolute",
                  right: 22,
                  top: 18,
                  width: 38,
                  zIndex: 1,
                  "&:hover": { backgroundColor: "#D90F1A" },
                }}
              >
                <CloseIcon sx={{ fontSize: 28 }} />
              </IconButton>
              <div className="mx-auto flex h-28 w-28 items-center justify-center rounded-full bg-[#F31723] text-white">
                <DeleteOutlineIcon sx={{ fontSize: 62 }} />
              </div>
              <span className="mt-8 block text-[28px] font-extrabold leading-tight text-black">
                Delete Advertising Option
              </span>
            </DialogTitle>
            <DialogContent className="px-0 pt-5">
              <p className="mx-auto max-w-[500px] text-center text-[19px] leading-8 text-black">
                Are you sure you want to delete the advertising option for{" "}
                {optionPendingDelete.name}?
              </p>
            </DialogContent>
            <DialogActions
              className="px-0 pb-0 pt-6"
              sx={{
                display: "flex",
                gap: 3,
                justifyContent: "center",
                width: "100%",
                "& > :not(style) ~ :not(style)": {
                  marginLeft: 0,
                },
              }}
            >
              <Button
                variant="contained"
                disabled={deleting}
                startIcon={<DeleteOutlineIcon sx={{ fontSize: 30 }} />}
                onClick={() => void handleConfirmDelete()}
                sx={{
                  backgroundColor: "#F31723",
                  borderRadius: "10px",
                  boxShadow: "0 5px 12px rgba(0,0,0,0.25)",
                  color: "white",
                  fontSize: 18,
                  fontWeight: 800,
                  height: 58,
                  minWidth: 210,
                  textTransform: "uppercase",
                  "& .MuiButton-startIcon": { mr: 1.5 },
                  "&:hover": { backgroundColor: "#D90F1A" },
                }}
              >
                Delete
              </Button>
              <Button
                variant="contained"
                onClick={() => setOptionPendingDelete(null)}
                sx={{
                  backgroundColor: commonStyles.charcoal,
                  borderRadius: "10px",
                  boxShadow: "0 5px 12px rgba(0,0,0,0.25)",
                  color: "white",
                  fontSize: 18,
                  fontWeight: 800,
                  height: 58,
                  minWidth: 210,
                  textTransform: "uppercase",
                  "&:hover": { backgroundColor: "#111827" },
                }}
              >
                Cancel
              </Button>
            </DialogActions>
          </>
        ) : null}
      </Dialog>
    </InvoiceSection>
  );
};
